import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  visuallyHidden,
} from '../styles';
import { Container, Heading, HtmlContent, SectionFooter } from './ui';
import { slugify } from '../utils';

const StyledAccordion = styled.section`
  ${sectionMargins()};
  scroll-margin-top: 80px;

  ${minBreakpointQuery.small`
    scroll-margin-top: 90px;
  `}

  ${minBreakpointQuery.medium`
    scroll-margin-top: 105px;
  `}

  ${minBreakpointQuery.large`
    scroll-margin-top: 120px;
  `}

  ${minBreakpointQuery.xxlarge`
    scroll-margin-top: 130px;
  `}
`;

const StyledHeader = styled.header`
  text-align: center;
  margin-bottom: 20px;

  ${minBreakpointQuery.small`
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 40px;
  `}
`;

const StyledText = styled.p`
  ${fluidFontSize(
    '36px',
    '53px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  color: ${brandColours.tertiary};
  margin-top: 10px;
`;

const StyledItems = styled.div`
  ${maxBreakpointQuery.small`
    margin: 0 -20px;
  `};
`;

const StyledItem = styled.article`
  border-bottom: 1px solid ${brandColours.quaternary};
  padding: 20px 25px;

  ${minBreakpointQuery.small`
    padding: 25px 30px;
  `}

  ${minBreakpointQuery.large`
    padding: 30px 35px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-right: 40px;
    padding-left: 40px;
  `}

  &:first-of-type {
    border-top: 1px solid ${brandColours.quaternary};
  }
`;

const StyledSubHeading = styled.h3`
  position: relative;
  padding-right: 30px;
  font-weight: ${({ $display }) => ($display ? 'bold' : 'normal')};
  line-height: 1.5;
  cursor: pointer;
  ${fontSize(20)};

  ${minBreakpointQuery.small`
    padding-right: 35px;
    ${fontSize(25)};
  `}

  ${minBreakpointQuery.large`
    padding-right: 40px;
    ${fontSize(30)};
  `}

  &:after {
    content: ${({ $display }) => ($display ? "'-'" : "'+'")};
    position: absolute;
    top: 50%;
    right: 0;
    ${fontSize(30)};
    transform: translateY(-50%);

    ${minBreakpointQuery.small`
      ${fontSize(34)};
    `}

    ${minBreakpointQuery.large`
      ${fontSize(38)};
    `}
  }
`;

const StyledContent = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}

  ${({ $display }) => {
    if (!$display) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
`;

const Accordion = ({ heading, text, items, link }) => {
  const [activeItem, setActiveItem] = useState(0);

  return (
    <StyledAccordion id={slugify(heading)}>
      <Container>
        <StyledHeader>
          <Heading>{heading}</Heading>
          {text && <StyledText>{text}</StyledText>}
        </StyledHeader>
        <StyledItems>
          {items.map(({ heading, content }, i) => {
            const display = activeItem === i;

            return (
              <StyledItem key={i}>
                <StyledSubHeading
                  $display={display}
                  onClick={() => setActiveItem(display ? undefined : i)}
                >
                  {heading}
                </StyledSubHeading>
                <StyledContent content={content} $display={display} />
              </StyledItem>
            );
          })}
        </StyledItems>
        <SectionFooter link={link} />
      </Container>
    </StyledAccordion>
  );
};

export default Accordion;
